import { Avatar, Tooltip } from 'antd';
import type { TooltipProps } from 'antd';

export enum AvatarSizes {
  SMALL = 'small',
  DEFAULT = 'default',
  LARGE = 'large',
}

export type AvatarUser = {
  userUuid?: string;
  accountUuid?: string;
  email?: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
};

const AVATAR_STYLES = {
  small: {
    backgroundColor: 'var(--color-avatar-bg, #5a6a6a)',
    color: 'var(--color-avatar-text, #ffffff)',
    fontFamily: 'var(--font-family, Inter, sans-serif)',
    fontWeight: 600,
    verticalAlign: 'middle' as const,
    fontSize: 10,
  },
  default: {
    backgroundColor: 'var(--color-avatar-bg, #5a6a6a)',
    color: 'var(--color-avatar-text, #ffffff)',
    fontFamily: 'var(--font-family, Inter, sans-serif)',
    fontWeight: 600,
    verticalAlign: 'middle' as const,
    fontSize: 12,
  },
  large: {
    backgroundColor: 'var(--color-avatar-bg, #5a6a6a)',
    color: 'var(--color-avatar-text, #ffffff)',
    fontFamily: 'var(--font-family, Inter, sans-serif)',
    fontWeight: 600,
    verticalAlign: 'middle' as const,
    fontSize: 14,
  },
};

const sizePx = (size: AvatarSizes) => {
  switch (size) {
    case AvatarSizes.SMALL:
      return 24;
    case AvatarSizes.LARGE:
      return 40;
    default:
      return 32;
  }
};

const sizeStyle = (size: AvatarSizes) => {
  switch (size) {
    case AvatarSizes.SMALL:
      return AVATAR_STYLES.small;
    case AvatarSizes.LARGE:
      return AVATAR_STYLES.large;
    default:
      return AVATAR_STYLES.default;
  }
};

const tooltipBodyStyle = {
  fontFamily: 'var(--font-family, Inter, sans-serif)',
  fontSize: 14,
  fontWeight: 400,
  backgroundColor: 'var(--color-surface-raised, #ffffff)',
  color: 'var(--color-text-base, #2e2e2e)',
  border: '1px solid var(--color-border, #dbeae3)',
  borderRadius: 15,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
};

export const renderAvatar = (
  firstName: string,
  lastName: string,
  size: AvatarSizes,
  _userUuid?: string,
  userImage?: string,
  placement?: string,
  noTooltip?: boolean,
) => {
  if (!firstName) return null;

  const firstChar = firstName.charAt(0);
  const secChar = lastName ? lastName.charAt(0) : '';
  const displayName = `${firstName} ${lastName}`.trim();
  const avatarEl = (
    <Avatar
      className={`scope-avatar scope-avatar--${size}`}
      alt={displayName}
      style={sizeStyle(size)}
      size={sizePx(size)}
      src={
        userImage
          ? userImage.startsWith('data:')
            ? userImage
            : `data:image/png;base64,${userImage}`
          : undefined
      }
    >
      {!userImage && firstChar + secChar}
    </Avatar>
  );

  if (noTooltip) return avatarEl;

  return (
    <Tooltip
      title={displayName}
      placement={(placement as TooltipProps['placement']) || 'top'}
      arrow={false}
      styles={{ body: tooltipBodyStyle }}
    >
      {avatarEl}
    </Tooltip>
  );
};

export const renderUserAvatar = (user: AvatarUser, size: AvatarSizes) =>
  renderAvatar(
    user.firstName,
    user.lastName,
    size,
    user.userUuid,
    user.profilePicture,
  );
